/**
 * PAPPO — Main Application Router
 * Configura o routing principal da aplicação com React Router.
 * Usa layout routes com ProtectedRoute (Outlet pattern).
 */

import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { NotificationsProvider } from './contexts/NotificationsContext';
import './App.css';

/* Pages — named imports */
import { LoginPage } from './pages/LoginPage';
import { ForgotPasswordPage } from './pages/ForgotPasswordPage';
import { ResetPasswordPage } from './pages/ResetPasswordPage';
import { ChatPage } from './pages/ChatPage';
import { ContactsPage } from './pages/ContactsPage';
import { ParentPanelPage } from './pages/ParentPanelPage';
import { PermissionsPage } from './pages/PermissionsPage';
import { ProfilePage } from './pages/ProfilePage';
import { InvitePage } from './pages/InvitePage';
import { VerificationPage } from './pages/VerificationPage';
import { InstructionsPage } from './pages/InstructionsPage';
import { AdminPage } from './pages/AdminPage';
import { StaffConsolePage } from './pages/StaffConsolePage';

/* Layout */
import { ProtectedRoute } from './components/layout/ProtectedRoute';
import { Navbar } from './components/layout/Navbar';
import { MainNav } from './components/layout/MainNav';
import { ModerationNotice } from './components/moderation/ModerationNotice';
import { useNavLayout } from './hooks/useNavLayout';

/**
 * Shell das rotas autenticadas: barra superior (Navbar) + navegação principal
 * (MainNav) — que aparece como TRILHA LATERAL no PC ou BARRA INFERIOR no
 * celular / por preferência do utilizador. Ver useNavLayout.
 */
const contentStyle = {
  flex: 1,
  minWidth: 0,
  display: 'flex',
  flexDirection: 'column' as const,
  minHeight: 0,
  // Rola verticalmente quando a tela é mais alta que a área (Painel, Admin,
  // Verificação). O Chat tem rolagem própria interna e preenche 100% da área.
  overflowY: 'auto' as const,
  overflowX: 'hidden' as const,
};

function AuthenticatedLayout() {
  const { navPos, setNavPos, isMobile, variant } = useNavLayout();
  return (
    <NotificationsProvider>
      <div className="app-shell">
        {/* Aviso de advertência da moderação (F3), se houver pendente */}
        <ModerationNotice />
        <Navbar navPos={navPos} setNavPos={setNavPos} showToggle={!isMobile} />
        {variant === 'rail' ? (
          <div style={{ flex: 1, display: 'flex', minHeight: 0 }}>
            <MainNav variant="rail" />
            <main style={contentStyle}>
              <ProtectedRoute />
            </main>
          </div>
        ) : (
          <>
            <main style={contentStyle}>
              <ProtectedRoute />
            </main>
            <MainNav variant="bottom" />
          </>
        )}
      </div>
    </NotificationsProvider>
  );
}

/**
 * App principal — Router + AuthProvider
 */
function App() {
  return (
    <AuthProvider>
      <ThemeProvider>
        <BrowserRouter>
          <Routes>
            {/* Rotas públicas — cadastro vive SÓ na InvitePage (A2) */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/invite/:token" element={<InvitePage />} />

            {/* Rotas protegidas com layout */}
            <Route element={<AuthenticatedLayout />}>
              <Route path="/chat" element={<ChatPage />} />
              <Route path="/contacts" element={<ContactsPage />} />
              {/* Só responsável/admin: painel parental e permissões (criança
                  cai em /chat). O backend já recusa /api/parent/* com 403 —
                  isto fecha a defesa-em-profundidade e evita telas confusas. */}
              <Route element={<ProtectedRoute allowedRoles={['parent', 'admin']} />}>
                <Route path="/parent-panel" element={<ParentPanelPage />} />
                <Route path="/permissoes" element={<PermissionsPage />} />
              </Route>
              <Route path="/perfil" element={<ProfilePage />} />
              <Route path="/staff" element={<StaffConsolePage />} />
              <Route path="/verificacao" element={<VerificationPage />} />
              <Route path="/instrucoes" element={<InstructionsPage />} />
              {/* Só admin: fila KYC + break-glass */}
              <Route element={<ProtectedRoute allowedRoles={['admin']} />}>
                <Route path="/admin" element={<AdminPage />} />
              </Route>
            </Route>

            {/* Redirect root → chat */}
            <Route path="/" element={<Navigate to="/chat" replace />} />

            {/* 404 → redirect to chat */}
            <Route path="*" element={<Navigate to="/chat" replace />} />
          </Routes>
        </BrowserRouter>
      </ThemeProvider>
    </AuthProvider>
  );
}

export default App;
